import { Box, Button, Typography, useTheme } from '@mui/material';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import Mui from 'materials';
import React, { useMemo, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';

import Divider from 'components/divider';
import InputNumber from 'components/InputNumber';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import NumberField from 'components/number-field';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';

import { isValidPositive } from 'utils/validate-numbers';
import { Feeder } from 'views/production-systems/features/farm-setup/types/feeders.type';
import { UpdateFeedersInputForm } from 'views/production-systems/features/farm-setup/types/update-feeders-input.type';
import { useFeedersState } from 'views/self-service/features/feeders/context/feeders-context';

type Props = {
  fieldIndex: number;
  pondCurrentBiomass: number;
};

const PAGE_NAME_ID = 'productionSystems.farmSetup.dialogs';

const ListFeeders: React.FC<Props> = ({ fieldIndex, pondCurrentBiomass }) => {
  const intl = useIntl();
  const theme = useTheme();
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [searchValue, setSearchVale] = useState<string>('');
  const { isLoading, feeders, feederBrands } = useFeedersState();

  // #region FORMS
  const {
    reset,
    handleSubmit,
    formState: { isValid },
    control: feederFormControl,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      id: 0,
      noUnits: 1,
    },
  });

  const {
    watch,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<UpdateFeedersInputForm>();

  const {
    update,
    prepend,
    fields: rows,
  } = useFieldArray({
    control: control,
    name: `ponds.${fieldIndex}.assignedFeeders`,
    keyName: '_id',
  });

  const watchFeeders = watch(`ponds.${fieldIndex}.assignedFeeders`);
  // #endregion

  // #region HANDLERS

  const handleAddFeeder = (formData: { id: number; noUnits: number }) => {
    const { id: feederId, noUnits: feederQuantity } = formData;

    const allFeeders = getValues(`ponds.${fieldIndex}.assignedFeeders`);
    const existingFeederIndex = allFeeders.findIndex((feeder) => feeder.feederId === feederId);

    if (existingFeederIndex !== -1) {
      const feederToUpdate = allFeeders[existingFeederIndex];
      update(existingFeederIndex, {
        ...feederToUpdate,
        numberOfFeeders: feederQuantity,
        isDeleted: false,
        edit: false,
      });
      reset();

      return;
    }

    const feederToAdd = feeders?.find((feeder) => feeder.id == feederId) as Feeder;

    if (!feederToAdd) return;

    prepend({
      feederId: feederToAdd.id,
      feederName: feederToAdd.name,
      feederBrand: feederToAdd.feederBrand,
      storageCapacity: feederToAdd.storageCapacity,
      feedingCapacity: feederToAdd.feedingCapacity,
      numberOfFeeders: feederQuantity,
      feederRegDate: '',
      edit: false,
      isDeleted: false,
      isNew: true,
    });

    reset();
  };

  const handleDeleteFeeder = (rowIndex: number) => {
    const feeder = getValues(`ponds.${fieldIndex}.assignedFeeders.${rowIndex}`);
    setValue(`ponds.${fieldIndex}.assignedFeeders.${rowIndex}.numberOfFeeders`, 0);
    update(rowIndex, {
      ...feeder,
      isDeleted: true,
    });
  };

  const handleEditRow = (rowIndex: number) => {
    var feeder = getValues(`ponds.${fieldIndex}.assignedFeeders.${rowIndex}`);
    feeder.edit = !feeder.edit;
    setValue(`ponds.${fieldIndex}.assignedFeeders.${rowIndex}`, feeder);
  };

  const handleConfirm = (rowIndex: number) => {
    var feeder = getValues(`ponds.${fieldIndex}.assignedFeeders.${rowIndex}`);
    feeder.edit = !feeder.edit;
    setValue(`ponds.${fieldIndex}.assignedFeeders.${rowIndex}`, feeder);
  };
  // #endregion

  const filteredFeeders = useMemo(() => {
    return feeders
      ?.filter((feeder) => !feeder.hidden)
      .map((feeder) => {
        const isAdded = rows.some((row) => Number(row.feederId) == feeder.id && !row.isDeleted);
        return {
          ...feeder,
          isDisabled: isAdded,
          name: isAdded ? `${feeder.name} ${intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.added` })}` : feeder.name,
        };
      });
  }, [feeders, rows]);

  const options: HeadCell[] = [
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.name` }),
      id: 'name',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.brand` }),
      id: 'brand',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.noUnits` }),
      id: 'units',
      align: 'center',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.storageCapacity` }),
      id: 'storageCapacity',
      align: 'right',
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.feedingCapacity` }),
      id: 'feedingCapacity',
      align: 'right',
    },
    {
      label: '',
      id: 'actions',
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <Box
        gap={4}
        display={'flex'}
        justifyContent={'space-between'}
        flexDirection={openForm ? 'column' : 'row'}
        alignItems={!openForm ? 'center' : 'auto'}>
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" justifyContent={'space-between'}>
            <Typography variant="h6" noWrap>
              <FormattedMessage id={`${PAGE_NAME_ID}.feeders.currentCapacity`} values={{ biomass: pondCurrentBiomass || 0 }} />
            </Typography>
          </Box>
        </Box>
        {!openForm ? (
          <Button hidden={openForm} onClick={() => setOpenForm(!openForm)}>
            <FormattedMessage id={`${PAGE_NAME_ID}.feeders.addFeeders`} />
          </Button>
        ) : (
          <>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-evenly'} gap={4}>
              <Select
                dark
                nested
                fullWidth
                controller={{
                  name: 'id',
                  control: feederFormControl,
                  rules: {
                    validate: {
                      required: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.required' })),
                    },
                  },
                }}
                stopPropagation
                search={{
                  value: searchValue,
                  onChange: setSearchVale,
                }}
                onOpen={() => {
                  setSearchVale('');
                }}
                noResults={{
                  show: !options?.length,
                  text: <FormattedMessage id={'general.noResults'} />,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.feeder` })}>
                {_.chain(filteredFeeders)
                  .filter((item) => item.name.toLowerCase().includes(searchValue) && !item.hidden)
                  .sortBy((s) => s.name.toLowerCase())
                  .value()
                  .map((brand) => (
                    <RadioSelectItem
                      value={brand.id}
                      sx={{ width: '100%' }}
                      disabled={brand.isDisabled}
                      key={`${brand.name}_${brand.id}`}>
                      {brand.name}
                    </RadioSelectItem>
                  ))}
              </Select>
              <NumberField
                dark
                fullWidth
                controller={{
                  name: 'noUnits',
                  control: feederFormControl,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    min: { value: 1, message: intl.formatMessage({ id: 'form.rules.errorMessage' }) },
                    validate: {
                      positive: (value) => (isValidPositive(value) ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                    },
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.feeders.noUnits` })}
              />
            </Box>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={4}>
              <Button type="button" variant="outlined" color="darkBlue" onClick={() => setOpenForm(false)}>
                <FormattedMessage id={`${PAGE_NAME_ID}.feeders.close`} />
              </Button>
              <Button type="button" color="darkBlue" disabled={!isValid} onClick={handleSubmit(handleAddFeeder)}>
                <FormattedMessage id={'general.add'} values={{ type: '' }} />
              </Button>
            </Box>
          </>
        )}
      </Box>
      <Divider dark />
      <List
        dark
        isLoading={isLoading}
        noResult={{
          show: !rows?.length || !rows.some((feeder) => !feeder.isDeleted),
          text: <FormattedMessage id={'general.noResults'} />,
        }}
        options={options}>
        {rows.map((feeder, idx) => {
          const fieldName = `ponds.${fieldIndex}.assignedFeeders.${idx}.numberOfFeeders`;
          const hasError = !!errors.ponds?.[fieldIndex]?.assignedFeeders?.[idx]?.numberOfFeeders;

          if (feeder.isDeleted) return null;
          return (
            <ListRow key={idx}>
              <ListCell>{feeder.feederName}</ListCell>
              <ListCell align="left" capitalize>
                {feeder.feederBrand}
              </ListCell>
              <ListCell align="center">
                {watchFeeders[idx].edit == true ? (
                  <InputNumber
                    dark
                    disableUnderline
                    controller={{
                      control: control,
                      name: fieldName,
                      rules: {
                        required: true,
                        min: 1,
                        validate: {
                          isValid: (value) => isValidPositive(value),
                        },
                      },
                    }}
                    autoFocus={watchFeeders[idx].edit}
                    inputProps={{
                      style: {
                        textAlign: 'center',
                      },
                    }}
                    sx={{
                      background: 'white',
                      borderRadius: 1,
                      border: `1px solid ${hasError ? 'red' : theme.palette.darkBlue.main}`,
                      width: 55,
                    }}
                  />
                ) : (
                  watchFeeders[idx].numberOfFeeders
                )}
              </ListCell>
              <ListCell align="right">{feeder.storageCapacity}</ListCell>
              <ListCell align="right">{feeder.feedingCapacity}</ListCell>
              <ListCell align="right">
                <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={2}>
                  {watchFeeders[idx].edit == true ? (
                    <>
                      <Mui.IconButton
                        sx={{ background: theme.palette.lightBlue.o75 }}
                        onClick={() => {
                          handleEditRow(idx);
                        }}>
                        <X size={18} />
                      </Mui.IconButton>
                      <Mui.IconButton
                        disabled={hasError}
                        sx={{ background: theme.palette.lightBlue.o75 }}
                        onClick={() => handleConfirm(idx)}>
                        <Check size={18} />
                      </Mui.IconButton>
                    </>
                  ) : (
                    <>
                      <Mui.IconButton sx={{ background: theme.palette.lightBlue.o75 }} onClick={() => handleDeleteFeeder(idx)}>
                        <Trash2 size={18} />
                      </Mui.IconButton>
                      <Mui.IconButton
                        sx={{ background: theme.palette.lightBlue.o75 }}
                        onClick={() => {
                          handleEditRow(idx);
                        }}>
                        <Pencil size={18} />
                      </Mui.IconButton>
                    </>
                  )}
                </Box>
              </ListCell>
            </ListRow>
          );
        })}
      </List>
    </Box>
  );
};

export default React.memo(ListFeeders);
